// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import { Popup } from 'react-map-gl';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { useAppDispatch, useAppSelector, useAppStore } from 'Hooks';
import { setHovered, clearHovered, toggleSelected } from 'Features/fileSelection/fileSelectionSlice';
import { selectIsSelected } from 'Features/fileSelection/fileSelectionSelectors';
import { downloadFileThunk } from 'Features/fileSelection/downloadFileThunk';
import { canDownloadFile } from 'Utils/fileDownloadRules';
import downloadIcon from 'Assets/download-icon.png';
import type { FileFootprint } from '../footprintSelectors';
import stylesDef, { popupShell } from './styles';

interface CardContentProps {
  files: FileFootprint[];
  numbers: Map<string, string>;
  onClose: () => void;
}

const DOWNLOAD_LABELS: Record<FileSelectionKind, string> = {
  tif: '.tif', las: 'pointcloud', pano: '.zip', poleImage: '.zip',
};

// The click companion card (D48/D55): filename, download, and the dashboard
// card's pager (ProjectOverviewCard, AUT-6113) for overlap stacks. A single
// file was selected by the click itself; on a stack the click selects nothing
// and the explicit Select/Unselect button acts on the shown page.
export const FootprintCardContent: React.FC<CardContentProps> = ({ files, numbers, onClose }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const [page, setPage] = useState(0);

  // A new click (new stack) restarts at its first file. Keyed on the stack's
  // CONTENT, not the array identity - parent re-renders rebuild the array and
  // must not snap an open card back to page 1.
  const stackKey = files.map((f) => `${f.kind}-${f.id}`).join('|');
  useEffect(() => setPage(0), [stackKey]);
  const fp = files[Math.min(page, files.length - 1)];

  const shownSelected = useAppSelector((state) => (fp ? selectIsSelected(state, fp.kind, fp.id) : false));
  const project = useAppSelector((state) => state.project.project);
  const opsTrainer = useAppSelector((state) => 'opsTrainer' in state.auth.resources);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // The viewed page gets the transient focus highlight (outline lights, other
  // tifs fade) so "2 of 3" visibly points at its box - ArcGIS identify
  // behaviour (D48). Cleared on close only while still the card's own hover.
  useEffect(() => {
    if (!fp) return undefined;
    const { kind, id } = fp;
    dispatch(setHovered({ source: 'sidebar', files: [{ kind, id }] }));
    return () => {
      const { hovered } = store.getState().fileSelection;
      if (hovered?.source === 'sidebar' && hovered.files[0]?.kind === kind && hovered.files[0]?.id === id) {
        dispatch(clearHovered());
      }
    };
  }, [fp?.kind, fp?.id]);

  if (!fp) return null;

  // Pano/pole metas carry no gate fields; the gate passes their kinds through.
  const showDownload = canDownloadFile(fp.kind, 'adminUpload' in fp.meta ? fp.meta : {}, project, opsTrainer);

  const indexLabel = numbers.get(`${fp.kind}-${fp.id}`);

  return (
    <Box sx={styles.card} role="dialog" aria-label={fp.name}>
      <Box sx={styles.cardHeader}>
        {indexLabel && (
          <Typography sx={styles.cardIndex}>
            {indexLabel}
          </Typography>
        )}
        {/* The full canonical filename, wrapped - never ellipsized (Mikaela 09-03). */}
        <Typography sx={styles.cardName}>{fp.name}</Typography>
        {showDownload && (
          <IconButton
            aria-label={`Download ${DOWNLOAD_LABELS[fp.kind]}`}
            sx={styles.downloadButton}
            onClick={() => dispatch(downloadFileThunk({ kind: fp.kind, id: fp.id, name: fp.name }))}
          >
            <img src={downloadIcon} alt="" />
          </IconButton>
        )}
        <IconButton size="small" aria-label="Close" onClick={onClose} sx={styles.closeButton}>
          ✕
        </IconButton>
      </Box>
      {files.length > 1 && (
        <>
          {/* Explicit selection for stacks (D55): the click selected nothing,
              this button toggles the file the card shows. */}
          <Button
            variant={shownSelected ? 'contained' : 'outlined'}
            size="small"
            sx={styles.selectButton}
            onClick={() => dispatch(toggleSelected({ kind: fp.kind, id: fp.id }))}
          >
            {shownSelected ? 'Unselect' : 'Select'}
          </Button>
          <Box sx={styles.pagerRow}>
            <Button size="small" sx={styles.pagerButton} disabled={page === 0} onClick={() => setPage(page - 1)}>
              ‹ Prev
            </Button>
            <Typography sx={styles.pagerLabel}>{`${page + 1} of ${files.length}`}</Typography>
            <Button
              size="small"
              sx={styles.pagerButton}
              disabled={page === files.length - 1}
              onClick={() => setPage(page + 1)}
            >
              Next ›
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

interface PopupCardProps {
  files: FileFootprint[];
  numbers: Map<string, string>;
  lngLat: [number, number];
  onClose: () => void;
}

// Geographic anchoring via the map Popup (MapLasBBox precedent) keeps the card
// glued to the clicked spot through pan/zoom. The popup's own chrome is
// stripped (popupShell) so the card reads as one bubble with a tail.
export const FootprintPopupCard: React.FC<PopupCardProps> = ({ files, numbers, lngLat, onClose }) => (
  <Popup
    longitude={lngLat[0]}
    latitude={lngLat[1]}
    anchor="bottom"
    closeButton={false}
    closeOnClick={false}
    maxWidth="none"
    css={popupShell}
  >
    <FootprintCardContent files={files} numbers={numbers} onClose={onClose} />
  </Popup>
);

export default FootprintPopupCard;
